<template>
    <v-card id="photo" elevation="4" class="mb-8">
        <v-toolbar color="blue darken-4" dark flat dense>
            <v-toolbar-title>Your Photo</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn v-scroll-to="'#top'" dark text>
                <v-icon left>fas fa-chevron-up</v-icon>
                Top
            </v-btn>
            <span v-if="!HasCompletedAnyRegistrations && !HasRegisteredOrCurrentEnrolments" v-scroll-to="'#kickers'" class="fa-stack fa-md icon-info">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-info fa-stack-1x"></i>
            </span>
            <span v-else-if="PhotoVerified" v-scroll-to="'#kickers'" class="fa-stack fa-md icon-success">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-check fa-stack-1x"></i>
            </span>
            <span v-else-if="!PhotoUploaded" v-scroll-to="'#kickers'" class="fa-stack fa-md icon-error">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-exclamation fa-stack-1x"></i>
            </span>
            <span v-else-if="PhotoOutdated || PhotoRejected" v-scroll-to="'#kickers'" class="fa-stack fa-md icon-error">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-exclamation fa-stack-1x"></i>
            </span>
            <span v-else-if="!PhotoVerified" v-scroll-to="'#kickers'" class="fa-stack fa-md icon-info">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="far fa-clock fa-stack-1x"></i>
            </span>
        </v-toolbar>
        <v-card-text v-if="!HasCompletedAnyRegistrations && !HasRegisteredOrCurrentEnrolments">
            <p class="text-h6">Provide a photograph of yourself so we can order your College Card.</p>
            <v-alert type="info" outlined dense text>Once you have completed your enrolment, you will be able to upload a photo for use on your College Card</v-alert>
            <p v-if="IsJuly">If you have been to College before, your previous photograph may still be suitable. If you would like to update your photo, please do so by {{ PhotoUploadLimitDate | formatLongDate }}.  Otherwise, we will use your existing photo.</p>
            <p v-else>If you have been to college before, and already have a photo within the system, we will use this for your college card.</p>
            <p>If you have no suitable photograph or can't upload one then we can take a photo for you in College, when you visit any <a href="#" @click="GoToHelpPoint">Campus Help Point</a>.</p>
            <p>Only courses that are longer than {{ MinimumEnrolmentEligible }} days are eligible for a College Card.</p>
        </v-card-text>
        <v-card-text v-else-if="PhotoVerified">
            <v-row>
                <v-col v-if="PhotoOutdated && CanUploadPhoto">
                    <p class="text-h6">Want to update your photo?</p>
                    <p>We have your photo from the last time you were at College.</p>
                    <p>If you would like to update this photo, you can upload a new one below:</p>
                    <v-alert type="info" outlined dense text class="mt-4">This must be prior to {{ PhotoUploadLimitDate | formatLongDate }} in order for it to be used on your new College Card</v-alert>
                    <p>Only courses that are longer than {{ MinimumEnrolmentEligible }} days are eligible for a College Card.</p>
                    <photo-upload-button></photo-upload-button>
                </v-col>
                <v-col v-else>
                    <p class="text-h6">Provide a photograph of yourself so we can order your College Card.</p>
                    <v-alert type="success" outlined dense text>Your photo has been approved and will be used on your College Card</v-alert>
                </v-col>
                <v-col md="auto"><img class="photo" :src="PhotoContents" /></v-col>
            </v-row>
        </v-card-text>
        <v-card-text v-else-if="PhotoRejected">
            <v-row>
                <v-col>
                    <v-alert type="error" outlined dense text>Your photo has been rejected: {{ photoRecord.RejectionReason }}</v-alert>
                    <p v-if="CanUploadPhoto">Please provide another photo by uploading one below:</p>
                    <photo-upload-button v-if="CanUploadPhoto"></photo-upload-button>
                </v-col>
                <v-col md="auto"><img class="photo" :src="PhotoContents" /></v-col>
            </v-row>
        </v-card-text>
        <v-card-text v-else-if="PhotoUploaded">
            <v-row>
                <v-col>
                    <v-alert type="info" outlined dense text>Your photo is awaiting approval</v-alert>
                    <p>Your photo is currently being reviewed against <a href="#" @click="GoToGuidance">the criteria</a>.</p>
                    <p>We'll let you know the outcome as soon as possible.</p>
                </v-col>
                <v-col md="auto"><img class="photo" :src="PhotoContents" /></v-col>
            </v-row>
        </v-card-text>
        <v-card-text v-else>
            <p class="text-h6">Provide a photograph of yourself so we can order your College Card.</p>
            <p>Upload a photo that you're happy with and meets <a href="#" @click="GoToGuidance">the criteria</a>:</p>
            <photo-upload-button></photo-upload-button>
            <p>If you have no suitable photograph or can't upload one, we can take a photo for you in College when you visit any <a href="#" @click="GoToHelpPoint">Campus Help Point</a>.</p>
        </v-card-text>
        <v-card-actions v-if="!PhotoVerified || PhotoOutdated">
            <v-btn @click="GoToGuidance" color="info" dark small style="color:white !important">
                <v-icon left small>fas fa-info-circle</v-icon>
                Photo Guidance
            </v-btn>
            <v-btn @click="OpenVideoGuidance" color="info" dark small style="color:white !important">
                <v-icon left small>fas fa-info-circle</v-icon>
                Photo Guidance (Video)
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
    import moment from 'moment'

    import filtersMixin from '@/views/components/mixins/base/filters.js'
    import ctrlOpenMixin from '@/views/components/mixins/base/ctrlOpen.js'
    import enrolmentsMixin from '@/views/components/mixins/enterpriseDataApi/enrolments.js'
    import enrolmentRegistrationsMixin from '@/views/components/mixins/enrolmentRegistrations.js'
    import collegeCardsMixin from '@/views/components/mixins/collegeCards.js'
    import photosMixin from '@/views/components/mixins/photos.js'

    import photoUploadButton from '@/views/components/controls/PhotoUploadButton'

    export default {
        props: ['localSettings', 'collegeCardLocalSettings', 'enrolmentRegistrations', 'photoRecord', 'photo', 'enrolments'],
        mixins: [
            filtersMixin,
            ctrlOpenMixin,
            enrolmentsMixin,
            enrolmentRegistrationsMixin,
            collegeCardsMixin,
            photosMixin,
        ],
        components: {
            'photo-upload-button': photoUploadButton,
        },
        data() {
            return {
                collegeCardsLocalSettings: {},
            }
        },
        computed: {
            PhotoUploadLimitDate() { return moment(this.localSettings?.keyValuePairs?.PhotoUploadLimitDate); },
            MinimumEnrolmentEligible() { return this.collegeCardsLocalSettings?.keyValuePairs?.MinimumEnrolmentEligible; },
            PhotoOutdated() { return this.PhotoVerified && moment(this.photo.lastUpdated).isBefore(this.PhotoUploadLimitDate); },
            CanUploadPhoto() { return moment().isBefore(this.PhotoUploadLimitDate); },
            IsJuly() { return moment().month() == 6 },
        },
        mounted() {
            this.LoadCollegeCardsLocalSettings();
        },
        methods: {
            GoToHelpPoint() { this.CtrlOpen(`/here-to-help`); },
            GoToGuidance() { this.CtrlOpen(`/photo-guidance`); },
            OpenVideoGuidance() { window.open('https://youtu.be/rFOt2p4QfJs?si=ZwLqCm_Q4KhtSqut', '_blank'); }
        },
    }
</script>

<style>
    img.photo {
        border-radius: 5px;
        float: right; 
        margin-left: 10px; 
        max-width: 120px;
    }
</style>